import React, { useState, useEffect, useRef } from 'react';
import { HiOutlineDocumentText, HiTrash, HiChevronDown } from 'react-icons/hi';
import { MdOutlineDocumentScanner } from 'react-icons/md';
import { IoClose } from 'react-icons/io5';
import { motion, AnimatePresence } from 'framer-motion';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { useNavigate } from 'react-router-dom';
import { fetchCustomers, fetchCustomerProjects, searchCustomers } from '../redux/actions/customerActions';
import { clearCustomerProjects } from '../redux/slices/customerSlice';
import { createInvoice, fetchNextInvoiceNumber, downloadInvoice } from '../redux/actions/invoiceActions';
import { resetCreateSuccess } from '../redux/slices/invoiceSlice';
import toast from 'react-hot-toast';
import { determineTaxType, getStateFromGSTIN } from '../utils/gstHelper';
import { hasBillProjectPermission } from '../utils/permissionUtils';

interface InvoiceItem {
  id: string;
  itemDetail: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface BilledCompany {
  id: string;
  name: string;
  logo: string;
  addressLines: string[];
  email: string;
  phone: string;
  gstin: string;
  cin: string;
}

const CreateInvoice: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showCompanyList, setShowCompanyList] = useState(false);
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [customerPage, setCustomerPage] = useState(1);
  const [isLoadingMoreCustomers, setIsLoadingMoreCustomers] = useState(false);
  const [hasMoreCustomers, setHasMoreCustomers] = useState(true);
  const customerDropdownRef = useRef<HTMLDivElement>(null); // For click-outside detection (outer container)
  const customerScrollRef = useRef<HTMLDivElement>(null); // For scroll handling (inner scrollable area)
  const customerAnchorRef = useRef<HTMLDivElement>(null); // For measuring position to decide open direction
  const [customerDropdownOpenUpward, setCustomerDropdownOpenUpward] = useState(false);

  // Customer search state
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Project-related state
  const [showProjectList, setShowProjectList] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<any[]>([]);
  const [projectPage, setProjectPage] = useState(1);
  const [isLoadingMoreProjects, setIsLoadingMoreProjects] = useState(false);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const projectDropdownRef = useRef<HTMLDivElement>(null);
  const projectSelectorWrapperRef = useRef<HTMLDivElement>(null);
  const projectDropdownPanelRef = useRef<HTMLDivElement>(null);
  const billedCompanies: BilledCompany[] = [
    {
      id: 'mess',
      name: 'Manak Engineering and Software Solutions',
      logo: 'https://d9hz6473ndr7q.cloudfront.net/manak-logo.png',
      addressLines: [
        'E 71, DGS Society Plot -6. Sector -22, Dwarka',
        'New Delhi, Delhi - 110077, India',
      ],
      email: 'info@manakengineering.com',
      phone: '+91 9871698842',
      gstin: '07AFBPJ6861Q1ZV',
      cin: '',
    },
    {
      id: 'mespl',
      name: 'Manak Engineering Services Pvt Ltd',
      logo: 'https://d9hz6473ndr7q.cloudfront.net/manak-logo.png',
      addressLines: [
        'E-71, DGS Society , Plot -6, Sector -22, Dwarka',
        'New Delhi, Delhi - 110077, India',
      ],
      email: 'info@manakengineering.com',
      phone: '+91 9871698842',
      gstin: '07AAQCM7695A1Z1',
      cin: 'U71100DL2023PTC412812',
    },
  ];

  // Form state
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [invoiceNumber] = useState('MESPL/25-26/67'); // Removed setInvoiceNumber as it's not used
  const [invoiceSeries, setInvoiceSeries] = useState<'MESPL' | 'MESS'>('MESPL'); // New: Invoice series selector
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [term, setTerm] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [purchaseOrder, setPurchaseOrder] = useState('');
  const [customerGSTIN, setCustomerGSTIN] = useState('');
  const [items, setItems] = useState<InvoiceItem[]>([
    { id: '1', itemDetail: '', quantity: 1, rate: 0, amount: 0 },
  ]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(billedCompanies[1]?.id ?? ''); // Default to MESPL
  const selectedCompany = billedCompanies.find(company => company.id === selectedCompanyId) || billedCompanies[1];

  // Sync invoice series with selected company
  useEffect(() => {
    console.log('🏢 Company changed to:', selectedCompanyId);
    // Map company ID to invoice series
    if (selectedCompanyId === 'mess') {
      console.log('📝 Setting invoice series to MESS');
      setInvoiceSeries('MESS');
    } else if (selectedCompanyId === 'mespl') {
      console.log('📝 Setting invoice series to MESPL');
      setInvoiceSeries('MESPL');
    }
  }, [selectedCompanyId]);

  // Get customers from Redux store (from customers state, not users state)
  const {
    customers: customerRecords,
    loading: customersLoading,
    pagination,
    selectedCustomerProjects,
    projectsLoading,
    projectsPagination,
    searchResults,
    searching
  } = useAppSelector(state => state.customers);

  // Invoice state from Redux
  const { loading: invoiceLoading, error: invoiceError, createSuccess, nextInvoiceNumber, isFirstTime, fetchingNextNumber } = useAppSelector(state => state.invoice);

  // Get user for permission check
  const { user } = useAppSelector(state => state.auth);

  // State for manual invoice number input (first time only)
  const [manualInvoiceNumber, setManualInvoiceNumber] = useState<number | ''>('');

  // Determine which customer list to display (search results or regular list) and sort alphabetically
  const displayedCustomers = (customerSearchQuery.trim() ? searchResults : customerRecords)
    .slice() // Create a copy to avoid mutating original array
    .sort((a, b) => a.companyName.localeCompare(b.companyName));

  // Find selected customer data from all available sources (customerRecords or searchResults)
  const selectedCustomerData =
    customerRecords.find(customer => customer.id === selectedCustomer) ||
    searchResults.find(customer => customer.id === selectedCustomer) ||
    null;

  // Payment terms options
  const termOptions = [
    { value: '0', label: 'Due on Receipt' },
    { value: '15', label: 'Net 15' },
    { value: '30', label: 'Net 30' },
    { value: '45', label: 'Net 45' },
    { value: '60', label: 'Net 60' },
    { value: '90', label: 'Net 90' },
  ];

  // Fetch initial customers when component mounts
  useEffect(() => {
    console.log('CreateInvoice: Fetching initial customers...');
    dispatch(fetchCustomers({ page: 1, limit: 10 }))
      .unwrap()
      .then((response) => {
        console.log('CreateInvoice: Initial customers fetched', response);
        if (response?.pagination) {
          setHasMoreCustomers(response.pagination.currentPage < response.pagination.pages);
        }
      })
      .catch((error) => {
        console.error('CreateInvoice: Error fetching customers', error);
        toast.error('Failed to load customers');
      });
  }, [dispatch]);

  // Handle lazy loading of customers on scroll
  const handleCustomerScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;

    // Load more when user scrolls to bottom (with 50px threshold)
    if (
      scrollPosition >= scrollHeight - 50 &&
      !isLoadingMoreCustomers &&
      hasMoreCustomers &&
      !customersLoading
    ) {
      const nextPage = customerPage + 1;
      console.log('CreateInvoice: Loading more customers, page:', nextPage);

      setIsLoadingMoreCustomers(true);

      dispatch(fetchCustomers({ page: nextPage, limit: 10 }))
        .unwrap()
        .then((response) => {
          console.log('CreateInvoice: More customers fetched', response);
          setCustomerPage(nextPage);

          if (response?.pagination) {
            setHasMoreCustomers(response.pagination.currentPage < response.pagination.pages);
          } else {
            setHasMoreCustomers(false);
          }
        })
        .catch((error) => {
          console.error('CreateInvoice: Error loading more customers', error);
          toast.error('Failed to load more customers');
        })
        .finally(() => {
          setIsLoadingMoreCustomers(false);
        });
    }
  };

  // Handle customer search with debounce
  const handleCustomerSearch = (query: string) => {
    setCustomerSearchQuery(query);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If query is empty, just clear search results
    if (query.trim().length === 0) {
      setIsSearchingCustomers(false);
      return;
    }

    setIsSearchingCustomers(true);

    // Set new timeout for debounced search (300ms delay)
    searchTimeoutRef.current = setTimeout(() => {
      console.log('Searching customers for:', query.trim());
      dispatch(searchCustomers(query.trim()))
        .unwrap()
        .then(result => {
          console.log('Customer search results:', result);
        })
        .catch(error => {
          console.error('Customer search error:', error);
          toast.error('Error searching customers');
        })
        .finally(() => {
          setIsSearchingCustomers(false);
        });
    }, 300);
  };

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle click outside for project dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is on project button
      const projectButton = document.querySelector('[data-testid="project-dropdown-button"]');
      if (projectButton?.contains(target)) {
        return; // Let the button's onClick handle the toggle
      }

      // Close project dropdown if click is outside wrapper
      if (
        showProjectList &&
        projectSelectorWrapperRef.current &&
        !projectSelectorWrapperRef.current.contains(target)
      ) {
        setShowProjectList(false);
      }

      // Check if click is on customer button
      const customerButton = document.querySelector('[data-testid="customer-dropdown-button"]');
      if (customerButton?.contains(target)) {
        return; // Let the button's onClick handle the toggle
      }

      // Close customer dropdown if click is outside
      if (
        showCustomerList &&
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(target)
      ) {
        setShowCustomerList(false);
      }

      // Close company dropdown if click is outside
      const companyDropdown = (event.target as HTMLElement).closest('[data-testid="company-dropdown"]');
      if (!companyDropdown && showCompanyList) {
        setShowCompanyList(false);
      }
    };

    // Handle Escape key press
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowProjectList(false);
        setShowCustomerList(false);
        setShowCompanyList(false);
      }
    };

    // Ensure dropdown is fully visible with bottom breathing space
    const ensureVisibleInViewport = (element: HTMLElement, topGap = 90, bottomGap = 32) => {
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const maxBottom = viewportHeight - bottomGap;

      if (rect.bottom > maxBottom) {
        window.scrollBy({
          top: rect.bottom - maxBottom,
          behavior: 'smooth',
        });
        return;
      }

      if (rect.top < topGap) {
        window.scrollBy({
          top: rect.top - topGap,
          behavior: 'smooth',
        });
      }
    };

    if (showCustomerList && customerAnchorRef.current) {
      const rect = customerAnchorRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // dropdown max height: ~60px search bar + 240px list + buffer = ~320px; gap = 16px
      setCustomerDropdownOpenUpward(spaceBelow < 336);
    }

    if (showProjectList && projectDropdownPanelRef.current) {
      setTimeout(() => {
        if (projectDropdownPanelRef.current) {
          ensureVisibleInViewport(projectDropdownPanelRef.current, 90, 32);
        }
      }, 120);
    }

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    // Clean up event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showProjectList, showCustomerList, showCompanyList]);

  // Calculate due date when term changes
  useEffect(() => {
    if (invoiceDate && term) {
      const date = new Date(invoiceDate);
      date.setDate(date.getDate() + parseInt(term));
      setDueDate(date.toISOString().split('T')[0]);
    }
  }, [invoiceDate, term]);

  // Handle invoice creation success
  useEffect(() => {
    if (createSuccess) {
      // Reset success flag
      dispatch(resetCreateSuccess());
    }
  }, [createSuccess, dispatch]);

  // Fetch next invoice number when series changes
  useEffect(() => {
    if (invoiceSeries) {
      console.log('🔍 Fetching next invoice number for series:', invoiceSeries);
      dispatch(fetchNextInvoiceNumber(invoiceSeries))
        .unwrap()
        .then((result) => {
          console.log('✅ Next invoice number result:', result);
        })
        .catch((error) => {
          console.error('❌ Error fetching next invoice number:', error);
        });
      setManualInvoiceNumber(''); // Reset manual input when series changes
    }
  }, [invoiceSeries, dispatch]);

  // Check if user has permission to access this page
  const hasBillingPermission = hasBillProjectPermission(user);

  // State for amount paid
  const [taxPercentage] = useState<number>(18); // Fixed at 18%
  const [taxType, setTaxType] = useState<'IGST' | 'CGST_SGST'>('IGST'); // New: Tax type selector
  const [amountPaid, setAmountPaid] = useState<number>(0);
  const [notes, setNotes] = useState<string>('');
  const [termsConditions, setTermsConditions] = useState<string>('');  // Add new item row
  const addItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      itemDetail: '',
      quantity: 1,
      rate: 0,
      amount: 0,
    };
    setItems([...items, newItem]);
  };

  // Remove item row
  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  // Update item
  const updateItem = (id: string, field: keyof InvoiceItem, value: string | number) => {
    setItems(
      items.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          // Recalculate amount when quantity or rate changes
          if (field === 'quantity' || field === 'rate') {
            updatedItem.amount = updatedItem.quantity * updatedItem.rate;
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  // Calculate totals
  const subtotal = selectedProjects.reduce((sum, project) => {
    const qty = 1; // Fixed quantity
    const rate = project.rate || 0;
    return sum + (qty * rate);
  }, 0);

  // Discount is always 0
  const discountAmount = 0;

  const subtotalAfterDiscount = subtotal;

  // Calculate tax based on type
  const taxAmount = (subtotalAfterDiscount * taxPercentage) / 100;
  const cgstAmount = taxType === 'CGST_SGST' ? taxAmount / 2 : 0;
  const sgstAmount = taxType === 'CGST_SGST' ? taxAmount / 2 : 0;
  const igstAmount = taxType === 'IGST' ? taxAmount : 0;

  const grandTotal = subtotalAfterDiscount + taxAmount - amountPaid;

  // Handle customer selection change
  const handleCustomerSelect = (customerId: string) => {
    // Find customer in either displayedCustomers (search results or regular list)
    const customer = displayedCustomers.find(item => item.id === customerId) || null;
    setSelectedCustomer(customerId);
    const customerGSTIN = customer?.gstNumber || '';
    setCustomerGSTIN(customerGSTIN);
    setShowCustomerList(false);

    // Automatically determine tax type based on GSTIN comparison
    if (customerGSTIN && selectedCompany.gstin) {
      const autoTaxType = determineTaxType(selectedCompany.gstin, customerGSTIN);
      setTaxType(autoTaxType);

      const companyState = getStateFromGSTIN(selectedCompany.gstin);
      const customerState = getStateFromGSTIN(customerGSTIN);

      console.log(`🔍 Tax Type Auto-Detection:
        Company: ${selectedCompany.name}
        Company GSTIN: ${selectedCompany.gstin} (${companyState})
        Customer: ${customer?.companyName}
        Customer GSTIN: ${customerGSTIN} (${customerState})
        Tax Type: ${autoTaxType} ${autoTaxType === 'CGST_SGST' ? '(Same State - Intra-state)' : '(Different State - Inter-state)'}
      `);

      toast.success(`Tax type set to ${autoTaxType === 'IGST' ? 'IGST (Inter-state)' : 'CGST + SGST (Intra-state)'}`);
    }

    // Clear search query when a customer is selected
    setCustomerSearchQuery('');
    setIsSearchingCustomers(false);

    // Clear previously selected projects
    setSelectedProjects([]);
    dispatch(clearCustomerProjects());

    // Fetch projects for the selected customer
    if (customer) {
      console.log('Fetching projects for customer:', customer.companyName);
      setProjectPage(1);
      setHasMoreProjects(true);
      dispatch(fetchCustomerProjects({
        customerId: customer.id,
        customerName: customer.companyName,
        page: 1,
        limit: 10
      }))
        .unwrap()
        .then((response) => {
          console.log('Customer projects fetched:', response);
          if (response?.pagination) {
            setHasMoreProjects(response.pagination.page < response.pagination.totalPages);
          }
        })
        .catch((error) => {
          console.error('Error fetching customer projects:', error);
          toast.error('Failed to load projects for this customer');
        });
    }
  };

  // Handle lazy loading of projects on scroll
  const handleProjectScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;

    // Load more when user scrolls to bottom (with 50px threshold)
    if (
      scrollPosition >= scrollHeight - 50 &&
      !isLoadingMoreProjects &&
      hasMoreProjects &&
      !projectsLoading &&
      selectedCustomerData
    ) {
      const nextPage = projectPage + 1;
      console.log('Loading more projects, page:', nextPage);

      setIsLoadingMoreProjects(true);

      dispatch(fetchCustomerProjects({
        customerId: selectedCustomerData.id,
        customerName: selectedCustomerData.companyName,
        page: nextPage,
        limit: 10
      }))
        .unwrap()
        .then((response) => {
          console.log('More projects fetched:', response);
          setProjectPage(nextPage);

          if (response?.pagination) {
            setHasMoreProjects(response.pagination.page < response.pagination.totalPages);
          } else {
            setHasMoreProjects(false);
          }
        })
        .catch((error) => {
          console.error('Error loading more projects', error);
        })
        .finally(() => {
          setIsLoadingMoreProjects(false);
        });
    }
  };

  // Handle project selection (toggle checkbox)
  const handleProjectToggle = (project: any) => {
    setSelectedProjects(prev => {
      const isSelected = prev.some(p => p.id === project.id);
      if (isSelected) {
        return prev.filter(p => p.id !== project.id);
      } else {
        // Add with projectAmount from database
        // Rate comes from projectAmount field in database
        return [...prev, {
          ...project,
          rate: project.projectAmount || 0, // Use projectAmount from database
          hsnSac: ''
        }];
      }
    });
  };

  // Update project hsnSac (rate is read-only from database)
  const updateProjectField = (projectId: string, field: 'hsnSac', value: string) => {
    setSelectedProjects(prev =>
      prev.map(p => (p.id === projectId ? { ...p, [field]: value } : p))
    );
  };

  // Handle select all projects
  const handleSelectAllProjects = () => {
    if (selectedProjects.length === selectedCustomerProjects.length) {
      setSelectedProjects([]);
    } else {
      // Add all projects with rate from projectAmount
      const projectsWithDefaults = selectedCustomerProjects.map(project => ({
        ...project,
        rate: project.projectAmount || 0, // Use projectAmount from database
        hsnSac: ''
      }));
      setSelectedProjects(projectsWithDefaults);
    }
  };

  // Handle clear all selected projects
  const handleClearAllProjects = () => {
    setSelectedProjects([]);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    if (selectedProjects.length === 0) {
      toast.error('Please select at least one project');
      return;
    }
    // Rate validation removed - rates are automatically set from database (projectAmount)

    // Validate manual invoice number if first time
    if (isFirstTime && !manualInvoiceNumber) {
      toast.error('Please enter the starting invoice number');
      return;
    }

    try {
      // Prepare the payload matching the backend schema
      const payload: any = {
        invoiceSeries: invoiceSeries,
        invoiceDate: invoiceDate,
        term: term || undefined,
        dueDate: dueDate || undefined,
        purchaseOrder: purchaseOrder || undefined,
        billedFrom: selectedCompany.name,
        billedToCustomerId: selectedCustomer,
        discount: 0, // Always 0 - discount removed from UI
        // taxType is auto-determined by backend based on GSTIN comparison
        amountPaid: Math.round(amountPaid),
        notes: notes || undefined,
        termsAndConditions: termsConditions || undefined,
        items: selectedProjects.map(project => ({
          projectId: project.id,
          quantity: 1, // Always 1 - quantity field removed from UI
          hsnSac: project.hsnSac || undefined,
        })),
      };

      // Add tax fields based on auto-determined type (for display purposes)
      // Backend will re-calculate and use the correct type
      if (taxType === 'IGST') {
        payload.tax = Math.round(taxAmount);
      } else if (taxType === 'CGST_SGST') {
        payload.cgst = Math.round(cgstAmount);
        payload.sgst = Math.round(sgstAmount);
      }

      // Add manual invoice number if first time
      if (isFirstTime && manualInvoiceNumber) {
        payload.invoiceNumber = Number(manualInvoiceNumber);
      }

      console.log('Submitting invoice:', payload);

      // Step 1: Create invoice and get the invoice ID
      const result = await dispatch(createInvoice(payload)).unwrap();

      toast.success('Invoice created successfully!');

      // Step 2: Download the invoice PDF
      if (result?.data?.id) {
        try {
          await dispatch(downloadInvoice(result.data.id)).unwrap();
          toast.success('Invoice downloaded successfully!');
        } catch (downloadError) {
          console.error('Error downloading invoice:', downloadError);
          toast.error('Invoice created but failed to download. You can download it from billing history.');
        }
      }

      // Step 3: Redirect to billing history page
      setTimeout(() => {
        navigate('/billing-history');
      }, 1000);

    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create invoice');
    }
  };

  // Check permission before rendering the page
  if (!hasBillingPermission) {
    return (
      <ComponentWrapper
        title={
          <div className="flex items-center gap-3">
            <MdOutlineDocumentScanner />
            <span>Create Invoice</span>
          </div>
        }
      >
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <div className="bg-red-50 rounded-full p-6 mb-4">
            <IoClose className="text-6xl text-red-500" />
          </div>
          <h3 className="text-2xl font-semibold text-gray-800 mb-2">Access Denied</h3>
          <p className="text-gray-600 max-w-md">
            You do not have permission to create invoices. Please contact your administrator if you believe this is an error.
          </p>
        </div>
      </ComponentWrapper>
    );
  }

  return (
    <ComponentWrapper
      title={
        <div className="flex items-center gap-3">
          <MdOutlineDocumentScanner />
          <span>Create Invoice</span>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Company Information */}
        <div className="font-inter">
          <div className="grid gap-8 md:grid-cols-2 md:gap-12">
            <div className="space-y-5">
              <span className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">Billed From</span>

              {/* Company Selection Dropdown */}
              <div className="relative" data-testid="company-dropdown">
                <button
                  type="button"
                  onClick={() => setShowCompanyList(!showCompanyList)}
                  className="flex items-center gap-2 text-3xl font-bold text-[#E14732] hover:text-red-700 transition-colors"
                >
                  <span>{selectedCompany.name}</span>
                  <HiChevronDown className="text-xl mt-1" />
                </button>

                {/* Company Dropdown List */}
                {showCompanyList && (
                  <div className="absolute top-full left-0 mt-2 w-full max-w-md bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                    {billedCompanies
                      .filter(company => company.id !== selectedCompanyId)
                      .map(company => (
                        <button
                          key={company.id}
                          type="button"
                          onClick={() => {
                            setSelectedCompanyId(company.id);
                            setShowCompanyList(false);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-red-50 transition-colors border-b last:border-b-0"
                        >
                          <div className="font-semibold text-gray-800">{company.name}</div>
                          <div className="text-xs text-gray-500 mt-1">{company.email}</div>
                        </button>
                      ))}
                  </div>
                )}
              </div>

              {/* Company Details */}
              <div className="text-gray-500 text-sm font-normal leading-6">
                {selectedCompany.addressLines.map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
              <div className="flex gap-4 items-center text-sm font-normal text-gray-500">
                <span>{selectedCompany.email}</span>
                <span className="text-gray-400">|</span>
                <span>{selectedCompany.phone}</span>
              </div>
              <div className="text-sm font-normal text-gray-500">
                <div><span className='font-medium'>GSTIN</span> - {selectedCompany.gstin}</div>
                {selectedCompany.cin && (
                  <div><span className='font-medium'>CIN No</span> - {selectedCompany.cin}</div>
                )}
              </div>
            </div>

            <div className="flex flex-col items-start gap-2 md:items-end md:justify-end">
              <span className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">Invoice Series: {invoiceSeries}</span>

              {/* Invoice Number Display/Input */}
              <div className="mt-3">
                {fetchingNextNumber ? (
                  <span className="text-sm text-gray-500 italic">Loading invoice number...</span>
                ) : isFirstTime ? (
                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-gray-600">Enter starting invoice number:</span>
                    <input
                      type="number"
                      value={manualInvoiceNumber}
                      onChange={(e) => setManualInvoiceNumber(e.target.value ? Number(e.target.value) : '')}
                      onWheel={(e) => e.currentTarget.blur()}
                      placeholder="e.g., 1"
                      min="1"
                      className="px-3 py-2 text-2xl font-bold text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#E14732] w-32"
                    />
                    <span className="text-xs text-gray-500">
                      Will be: {invoiceSeries}/{nextInvoiceNumber?.financialYear}/{manualInvoiceNumber || '?'}
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    <span className="text-3xl font-bold text-gray-900">
                      {nextInvoiceNumber?.formattedNumber || 'Loading...'}
                    </span>
                    <span className="text-xs text-gray-500">Next invoice number (auto-generated)</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Customer and Invoice Details */}
        <div className="bg-white rounded-lg pt-6">
          <div className="flex gap-14 items-start">
            {/* LEFT: BILL TO SECTION */}
            <div className="w-2/5">
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 mb-3">BILLED TO</div>

              {/* Customer Selection Dropdown */}
              <div className="relative" ref={customerAnchorRef}>
                <button
                  type="button"
                  data-testid="customer-dropdown-button"
                  onClick={() => setShowCustomerList(!showCustomerList)}
                  className="flex items-center text-left gap-2 text-3xl font-bold text-[#E14732] hover:text-red-700 transition-colors"
                >
                  <span>{selectedCustomerData?.companyName || 'Select Customer'}</span>
                  <HiChevronDown className="text-xl mt-1" />
                </button>

                {/* Customer Dropdown List */}
                {showCustomerList && (
                  <div
                    ref={customerDropdownRef}
                    className={`absolute left-0 w-full max-w-md bg-white border border-gray-200 rounded-lg shadow-lg z-10 ${
                      customerDropdownOpenUpward ? 'bottom-full mb-2' : 'top-full mt-2'
                    }`}
                  >
                    {/* Search Input - Sticky at top */}
                    <div className="sticky top-0 bg-white border-b border-gray-200 p-3 z-10">
                      <div className="relative">
                        <input
                          type="text"
                          value={customerSearchQuery}
                          onChange={(e) => handleCustomerSearch(e.target.value)}
                          placeholder="Search customer by company name..."
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E14732] focus:border-[#E14732]"
                          autoFocus
                        />
                        <svg
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        {isSearchingCustomers && (
                          <svg
                            className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin h-4 w-4 text-[#E14732]"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        )}
                      </div>
                    </div>

                    {/* Scrollable Customer List */}
                    <div
                      ref={customerScrollRef}
                      onScroll={handleCustomerScroll}
                      className="max-h-60 overflow-y-auto"
                    >
                      {displayedCustomers.length > 0 ? (
                        <>
                          {displayedCustomers.map(customer => (
                            <button
                              key={customer.id}
                              type="button"
                              onClick={() => handleCustomerSelect(customer.id)}
                              className="w-full text-left px-4 py-3 hover:bg-red-50 transition-colors border-b last:border-b-0"
                            >
                              <div className="font-semibold text-gray-800">{customer.companyName}</div>
                              <div className="text-xs text-gray-500 mt-1">{customer.businessEmail || customer.billingEmail}</div>
                            </button>
                          ))}
                          {/* Loading indicator for lazy loading (only show when not searching) */}
                          {!customerSearchQuery && isLoadingMoreCustomers && (
                            <div className="px-4 py-3 text-center text-sm text-gray-500">
                              <div className="flex items-center justify-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-[#E14732]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Loading more...
                              </div>
                            </div>
                          )}
                          {/* End of list indicator (only show when not searching) */}
                          {!customerSearchQuery && !hasMoreCustomers && customerRecords.length > 10 && (
                            <div className="px-4 py-2 text-center text-xs text-gray-400">
                              All customers loaded
                            </div>
                          )}
                        </>
                      ) : (isSearchingCustomers || searching) ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-500">
                          <div className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-4 w-4 text-[#E14732]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {customerSearchQuery ? 'Searching...' : 'Loading customers...'}
                          </div>
                        </div>
                      ) : customerSearchQuery ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-500">
                          No customers found for "{customerSearchQuery}"
                        </div>
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500">No customers available</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Customer Details */}
              {selectedCustomerData && (
                <>
                  <div className="text-gray-500 mt-4 text-sm font-normal leading-6">
                    {selectedCustomerData.ownerName && (
                      <div >{selectedCustomerData.ownerName}</div>
                    )}
                    {selectedCustomerData.clientAddress && (
                      <>
                        <div className="mt-1">{selectedCustomerData.clientAddress.address}</div>
                        <div>
                          {selectedCustomerData.clientAddress.city} - {selectedCustomerData.clientAddress.pinCode}, {selectedCustomerData.clientAddress.state}, {selectedCustomerData.clientAddress.country}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex gap-4 mt-4 items-center text-sm text-gray-500">
                    <span>{selectedCustomerData.businessEmail || selectedCustomerData.billingEmail}</span>
                    {(selectedCustomerData.phone || selectedCustomerData.mobile) && (
                      <>
                        <span className="text-gray-400">|</span>
                        <span>{selectedCustomerData.phone || selectedCustomerData.mobile}</span>
                      </>
                    )}
                  </div>
                  {/* GST Number */}
                  {selectedCustomerData.gstNumber && (
                    <div className="text-sm text-gray-500 mt-2">
                      <span className="font-medium">GSTIN</span> - {selectedCustomerData.gstNumber}
                    </div>
                  )}
                </>
              )}

              {/* Project Selection Dropdown (Compact) */}
              <div ref={projectSelectorWrapperRef} className="relative mt-5">
                <button
                  type="button"
                  data-testid="project-dropdown-button"
                  onClick={() => {
                    if (!selectedCustomer) {
                      toast.error('Please select a customer first');
                      return;
                    }
                    setShowProjectList(!showProjectList);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${showProjectList
                    ? 'bg-[#E14732] text-white border-[#E14732]'
                    : 'bg-white text-[#E14732] border-[#E14732] hover:bg-red-50'
                    }`}
                >
                  <div className="flex items-center gap-2.5">
                    <MdOutlineDocumentScanner className={`text-lg ${showProjectList ? 'text-white' : 'text-[#E14732]'}`} />
                    <span className="font-semibold text-base">
                      {selectedProjects.length > 0
                        ? `${selectedProjects.length} / ${projectsPagination?.total ?? selectedCustomerProjects.length} Selected`
                        : projectsPagination?.total != null
                        ? `Add Project (${projectsPagination.total})`
                        : 'Add Project'}
                    </span>
                  </div>
                  <HiChevronDown className={`text-lg ${showProjectList ? 'text-white rotate-180' : 'text-[#E14732]'} transition-transform`} />
                </button>

                <AnimatePresence>
                  {showProjectList && (
                    <motion.div
                      ref={projectDropdownPanelRef}
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="relative mt-2 w-full bg-white border border-[#E14732] rounded-lg shadow-sm overflow-hidden"
                      style={{ maxHeight: '320px' }}
                    >
                      <div
                        ref={projectDropdownRef}
                        onScroll={handleProjectScroll}
                        className="overflow-y-auto project-selector-scroll"
                        style={{ maxHeight: '320px', scrollbarGutter: 'stable' }}
                      >
                        {selectedCustomerProjects.length > 0 ? (
                          <>
                            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-2.5 flex items-center justify-between z-10">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedProjects.length === selectedCustomerProjects.length && selectedCustomerProjects.length > 0}
                                  onChange={handleSelectAllProjects}
                                  className="w-4 h-4 text-[#E14732] border-gray-300 rounded focus:ring-[#E14732]"
                                />
                                <span className="font-semibold text-sm text-gray-700">SELECT ALL</span>
                              </label>
                              <button
                                type="button"
                                onClick={handleClearAllProjects}
                                className="text-[#E14732] text-sm font-semibold hover:underline"
                              >
                                CLEAR
                              </button>
                            </div>

                            {selectedCustomerProjects.map((project) => (
                              <label
                                key={project.id}
                                className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 cursor-pointer border-b last:border-b-0 transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedProjects.some(p => p.id === project.id)}
                                  onChange={() => handleProjectToggle(project)}
                                  className="w-4 h-4 text-[#E14732] border-gray-300 rounded focus:ring-[#E14732]"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-sm text-gray-900 truncate">{project.projectName || 'Unnamed Project'}</div>
                                  <div className="text-xs text-gray-500 mt-0.5">{"M" + project.manakProjectId || 'No MID'}</div>
                                </div>
                              </label>
                            ))}

                            {isLoadingMoreProjects && (
                              <div className="px-4 py-3 text-center">
                                <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                                  <svg className="animate-spin h-4 w-4 text-[#E14732]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Loading more projects...
                                </div>
                              </div>
                            )}

                            {!hasMoreProjects && selectedCustomerProjects.length > 10 && (
                              <div className="px-4 py-2 text-center text-xs text-gray-400">
                                All projects loaded
                              </div>
                            )}
                          </>
                        ) : projectsLoading ? (
                          <div className="px-4 py-6 text-center">
                            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                              <svg className="animate-spin h-4 w-4 text-[#E14732]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Loading projects...
                            </div>
                          </div>
                        ) : (
                          <div className="px-4 py-6 text-center text-sm text-gray-500">
                            No projects found for this customer
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* RIGHT: INVOICE SECTION */}
            <div className="flex-1 flex flex-col gap-6">
              {/* Invoice Date row */}
              <div className="flex justify-end">
                <div className="flex items-center border border-gray-100 rounded overflow-hidden w-fit">
                  <label className="text-base text-gray-500 italic font-semibold bg-gray-100 px-5 py-2.5 whitespace-nowrap">
                    Invoice Date
                  </label>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    className="bg-white px-5 py-2.5 font-semibold text-gray-600 text-base focus:outline-none cursor-pointer border-none min-w-[200px]"
                  />
                </div>
              </div>

              {/* Term Select row */}
              <div className="flex justify-end">
                <div className="flex items-center border border-gray-100 rounded overflow-hidden w-fit">
                  <label className="text-base text-gray-500 italic font-semibold bg-gray-100 px-5 py-2.5 whitespace-nowrap">
                    Term
                  </label>
                  <div className="relative">
                    <select
                      value={term}
                      onChange={(e) => setTerm(e.target.value)}
                      className="bg-white px-5 py-2.5 pr-10 text-gray-500 font-medium focus:outline-none appearance-none cursor-pointer border-none min-w-[200px]"
                    >
                      <option value="">Select Term</option>
                      {termOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <HiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Due Date */}
              <div className="flex justify-end">
                <div className="flex items-center border border-gray-100 rounded overflow-hidden w-fit">
                  <label className="text-base text-gray-500 italic font-semibold bg-gray-100 px-5 py-2.5 whitespace-nowrap">
                    Due Date
                  </label>
                  {dueDate ? (
                    <div className="bg-white px-5 py-2.5 font-semibold text-gray-500 text-base min-w-[200px]">
                      {new Date(dueDate).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      }).replace(/\//g, '-')}
                    </div>
                  ) : (
                    <div className="bg-white px-5 py-2.5 text-gray-400 italic flex items-center gap-2 min-w-[200px]">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      Note : Set Term First
                    </div>
                  )}
                </div>
              </div>

              {/* Purchase Order */}
              <div className="flex justify-end">
                <div className="flex items-center border border-gray-100 rounded overflow-hidden w-fit">
                  <label className="text-base text-gray-500 italic font-semibold bg-gray-100 px-5 py-2.5 whitespace-nowrap">
                    Purchase Order (P.O)
                  </label>
                  <input
                    className="bg-white px-5 py-2.5 text-gray-700 font-medium focus:outline-none border-none min-w-[200px]"
                    placeholder="Enter Purchase Order"
                    type="text"
                    value={purchaseOrder}
                    onChange={(e) => setPurchaseOrder(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Project Selection Section */}
        {selectedProjects.length > 0 && (
          <div className="bg-white rounded-lg pt-4">
            <div className="">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-red-50 rounded-t-lg border-gray-200 font-semibold text-[#E14732] text-sm">
                <div className="col-span-1 text-center">#</div>
                <div className="col-span-5">Item Details</div>
                <div className="col-span-2 text-center">HSN/SAC</div>
                <div className="col-span-2 text-center">Rate</div>
                <div className="col-span-2 text-right pr-4">Amount</div>
              </div>

              {/* Project Rows */}
              {selectedProjects.map((project, index) => {
                const qty = 1; // Fixed quantity
                const rate = project.rate || 0;
                const amount = qty * rate;

                return (
                  <div
                    key={project.id}
                    className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors items-center"
                  >
                    {/* Serial Number */}
                    <div className="col-span-1 text-center text-gray-600 font-medium">
                      {index + 1}
                    </div>

                    {/* Item Details */}
                    <div className="col-span-5">
                      <div className="font-semibold text-gray-900">{project.projectName || 'Unnamed Project'}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        <span className="font-medium">MID:</span> {project.manakProjectId != null ? `M${project.manakProjectId}` : 'N/A'}
                        <span className="text-gray-300 mx-2">|</span>
                        <span className="font-medium">Article No:</span> {project.projectNumber || 'N/A'}
                      </div>
                    </div>

                    {/* HSN/SAC Input */}
                    <div className="col-span-2 flex justify-center">
                      <input
                        type="text"
                        value={project.hsnSac || ''}
                        onChange={(e) => updateProjectField(project.id, 'hsnSac', e.target.value)}
                        placeholder="998391"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-[#E14732] focus:border-[#E14732]"
                      />
                    </div>

                    {/* Rate Display (Read-only from Database) */}
                    <div className="col-span-2 flex justify-center">
                      <div className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-right font-semibold text-gray-700">
                        ₹{rate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>

                    {/* Amount & Delete */}
                    <div className="col-span-2 flex items-center justify-end gap-3">
                      <div className="font-semibold text-gray-900">
                        ₹{amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleProjectToggle(project)}
                        className="text-[#E14732] hover:text-red-700 transition-colors"
                        title="Remove project"
                      >
                        <HiTrash className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Totals Section */}
              <div className="mt-6 border-t border-gray-200 pt-6 space-y-4">
                {/* Subtotal */}
                <div className="flex items-center justify-between px-6 py-2">
                  <span className="font-semibold text-gray-700">Subtotal</span>
                  <span className="font-semibold text-gray-900 text-lg">
                    ₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                {/* Tax Type - Auto-determined, Read-only Display */}
                <div className="flex items-center justify-between px-6 py-3 bg-blue-50 border-l-4 border-blue-500">
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-700">Tax Type (Auto-detected)</span>
                    <span className="text-xs text-gray-500 mt-1">
                      {taxType === 'IGST' ? 'Inter-state transaction' : 'Intra-state transaction (Same state)'}
                    </span>
                  </div>
                  <div className={`px-4 py-2 rounded-lg font-bold ${taxType === 'IGST'
                    ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                    : 'bg-green-100 text-green-700 border-2 border-green-300'
                    }`}>
                    {taxType === 'IGST' ? 'IGST' : 'CGST + SGST'}
                  </div>
                </div>

                {/* Tax Breakdown Display */}
                {taxType === 'IGST' ? (
                  <div className="flex items-center justify-between px-6 py-2 bg-gray-50">
                    <span className="font-semibold text-gray-700">IGST ({taxPercentage}%)</span>
                    <span className="font-semibold text-gray-700">
                      ₹{igstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between px-6 py-2 bg-gray-50">
                      <span className="font-semibold text-gray-700">CGST ({(taxPercentage / 2).toFixed(2)}%)</span>
                      <span className="font-semibold text-gray-700">
                        ₹{cgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between px-6 py-2 bg-gray-50">
                      <span className="font-semibold text-gray-700">SGST ({(taxPercentage / 2).toFixed(2)}%)</span>
                      <span className="font-semibold text-gray-700">
                        ₹{sgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </>
                )}

                {/* Amount Paid */}
                <div className="flex items-center justify-between px-6 py-2">
                  <span className="font-semibold text-gray-700">Amount Paid</span>
                  <div className="relative w-40">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={amountPaid}
                      onChange={(e) => setAmountPaid(parseFloat(e.target.value) || 0)}
                      onWheel={(e) => e.currentTarget.blur()}
                      placeholder="0.00"
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-right focus:outline-none focus:ring-2 focus:ring-[#E14732] focus:border-[#E14732]"
                    />
                  </div>
                </div>

                {/* Grand Total */}
                <div className="flex items-center justify-between px-6 py-4 mt-4 border-t border-gray-300">
                  <span className="text-xl font-bold text-gray-900">Grand Total</span>
                  <span className="text-2xl font-bold text-[#E14732]">
                    ₹{grandTotal.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              {/* Notes and Terms & Conditions */}
              <div className="grid md:grid-cols-2 gap-6 px-6 border-gray-200">
                {/* Notes */}
                <div>
                  <label className="block font-semibold text-gray-700 mb-3">Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Lorem ipsum dolor sit amet consectetur. Sed praesent in pulvinar eu augue nibh turpis. Dolor dui"
                    rows={3}
                    className="w-full px-4 py-3 text-sm text-gray-600 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E14732] focus:border-[#E14732] resize-none leading-relaxed"
                  />
                </div>

                {/* Terms & Conditions */}
                <div>
                  <label className="block font-semibold text-gray-700 mb-3">Terms & Conditions</label>
                  <textarea
                    value={termsConditions}
                    onChange={(e) => setTermsConditions(e.target.value)}
                    placeholder="Lorem ipsum dolor sit amet consectetur. Sed praesent in pulvinar eu augue nibh turpis. Dolor dui"
                    rows={3}
                    className="w-full px-4 py-3 text-sm text-gray-600 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E14732] focus:border-[#E14732] resize-none leading-relaxed"
                  />
                </div>
              </div>

              {/* Create Invoice Button */}
              <div className="mt-8">
                <button
                  type="submit"
                  disabled={invoiceLoading}
                  className="w-full flex items-center justify-center gap-3 px-6 py-8 bg-[#E14732] text-white rounded-lg font-semibold text-2xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <HiOutlineDocumentText className="text-2xl" />
                  <span>{invoiceLoading ? 'Creating Invoice...' : 'Create Invoice'}</span>
                </button>
              </div>
            </div>
          </div>
          )}
      </form>
    </ComponentWrapper>
  );
};

export default CreateInvoice;
